import React, { useState, useRef, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import MedicalAppointmentsContext from './context';
import LocalHospitalIcon from '@material-ui/icons/LocalHospital';
import List from '~/containers/List';
import Request from '~/providers/Request';
import ModalDelete from '~/components/ModalDelete';
import {
  newMedicalAppointments,
  showMedicalAppointments,
  editMedicalAppointments,
} from '~/config/routes';
import moment from 'moment';
import { AbilityContext } from '~/contexts/Can';

function MedicalAppointmentsList() {
  const { t } = useTranslation();
  const [modalInfo, setModalInfo] = useState({ show: false });
  const [update] = useState(false);
  const { delRecord } = useContext(MedicalAppointmentsContext);
  const ability = useContext(AbilityContext);
  const listEl = useRef(null);

  const priority = {
    low: t('medicalAppointments.priority.low'),
    medium: t('medicalAppointments.priority.medium'),
    high: t('medicalAppointments.priority.high'),
  };

  const status = {
    new: t('medicalAppointments.status.new'),
    pre_appointment: t('medicalAppointments.status.pre_appointment'),
    wait_room: t('medicalAppointments.status.wait_room'),
    wait_room_scheduled: t('medicalAppointments.status.wait_room_scheduled'),
    ongoing: t('medicalAppointments.status.ongoing'),
    on_schedule: t('medicalAppointments.status.on_schedule'),
    on_approval: t('medicalAppointments.status.on_approval'),
    in_consultation: t('medicalAppointments.status.in_consultation'),
    finished: t('medicalAppointments.status.finished'),
  };

  const priorityColor = {
    low: '#03D100',
    medium: '#FF6B00',
    high: '#E00023',
  };

  const statusColor = {
    new: '#AC92EA',
    pre_appointment: '#AC92EA',
    wait_room: '#AC92EA',
    wait_room_scheduled: '#2182F6',
    on_schedule: '#AC92EA',
    on_approval: '#AC92EA',
    in_consultation: '#123',
    ongoing: '#F9D159',
    finished: '#999999',
  };

  const trAttr = (attr) => t(`medicalAppointments.attributes.${attr}`);
  const trExtra = (extra) => t(`medicalAppointments.extras.${extra}`);

  function handleDeletionModal(id, title) {
    setModalInfo({ show: true, id, title });
  }

  async function handleDelete() {
    try {
      await Request.del(`/admin/medical_appointment/${modalInfo.id}`);
      delRecord(modalInfo.id);
      listEl.current.onQueryChange();
      toast.success(t('delete-toast-success'));
    } catch (e) {
      toast.error(t('delete-toast-fail'));
    } finally {
      setModalInfo({ show: false });
    }
  }

  const actions = ['search', 'filter'];

  if (ability.can('new', 'MedicalAppointmentsReports')) {
    actions.push('new');
  }
  if (ability.can('edit', 'MedicalAppointmentsReports')) {
    actions.push('edit');
  }
  if (ability.can('delete', 'MedicalAppointmentsReports')) {
    actions.push('del');
  }
  if (ability.can('show', 'MedicalAppointmentsReports')) {
    actions.push('show');
  }

  return (
    <div>
      <List
        deleteAction={handleDeletionModal}
        routeToEdit={editMedicalAppointments}
        routeToNew={newMedicalAppointments}
        routeToShow={showMedicalAppointments}
        listEl={listEl}
        path={'/admin/medical_appointment/reports'}
        actions={actions}
        paperProps={{
          Icon: LocalHospitalIcon,
          iconColor: 'blue',
          title: t('medicalAppointments.defPlural'),
        }}
        columns={[
          {
            title: trAttr('id'),
            field: 'id',
            type: 'numeric',
            precision: 0,
            includeThousandsSeparator: false,
          },
          { title: trAttr('user'), field: 'user_name', type: 'string' },
          {
            field: 'record_filename',
            type: 'boolean',
            title: trExtra('doctor_consulted'),
          },
          {
            field: 'status',
            type: 'string',
            title: trAttr('status'),
            // eslint-disable-next-line react/display-name
            render: (rowData) => {
              return (
                <label
                  style={{
                    backgroundColor: statusColor[rowData.status],
                    padding: 4,
                    color: 'white',
                    borderRadius: 4,
                    fontWeight: 500,
                  }}
                >{`${status[rowData.status]}`}</label>
              );
            },
          },
          { title: trAttr('doctor'), field: 'doctor_name', type: 'string' },
          { title: trAttr('notes'), field: 'notes', type: 'string' },
          {
            field: 'priority',
            type: 'string',
            title: trAttr('priority'),
            // eslint-disable-next-line react/display-name
            render: (rowData) => {
              return (
                <label
                  style={{
                    backgroundColor: priorityColor[rowData.priority],
                    padding: 4,
                    color: 'white',
                    borderRadius: 4,
                    fontWeight: 500,
                  }}
                >{`${priority[rowData.priority]}`}</label>
              );
            },
          },
          {
            title: trAttr('created_at'),
            field: 'created_at',
            type: 'datetime',
          },
          {
            title: trAttr('updated_at'),
            field: 'updated_at',
            type: 'datetime',
          },
        ]}
        context={MedicalAppointmentsContext}
        recordMap={(record) => ({
          ...record,
          //record_filename: record.record_filename ? 'Sim' : 'Não',
          created_at: record.created_at
            ? moment(record.created_at).format('DD/MM/YYYY HH:mm')
            : null,
          updated_at: record.updated_at
            ? moment(record.updated_at).format('DD/MM/YYYY HH:mm')
            : null,
        })}
        fetchFlag={update}
      />

      <ModalDelete
        id={modalInfo.id}
        name={modalInfo.title}
        modal={modalInfo.show}
        deleteAction={handleDelete}
        onClose={() => setModalInfo({ show: false })}
      />
    </div>
  );
}

export default React.memo(MedicalAppointmentsList);
